/** N244 Application Notice wizard (Dean, 22 Oct 2026) — Order sought → Details & wording → Enclosures & generate → Send to court.
 *  The official HMCTS form is filled field by field; Fill with AI reads the ticked files + the whole file; cover letter and draft order
 *  are ours (PDF in the bundle, .docx in OneDrive for Word edits); the bundle goes by Postworks and / or e-mail from legal@. */
import { desktopOnly } from "@/components/phone/DesktopOnlyNote";
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Gavel, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { getN244Context, n244AiDraft, n244AiRun, n244Compose } from "@/lib/api";
import { N244OrderStep } from "./N244OrderStep";
import { N244DetailsStep } from "./N244DetailsStep";
import { N244BundleStep } from "./N244BundleStep";
import { N244SendStep } from "./N244SendStep";
import { STEPS, errMsg, initialForm } from "./n244Shared";

function StepBar({ step, setStep, locked }) {
  const idx = STEPS.findIndex((s) => s.key === step);
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="n244-steps">
      {STEPS.map((s, i) => (
        <button key={s.key} type="button" disabled={locked} onClick={() => setStep(s.key)} data-testid={`n244-step-${s.key}`} aria-current={step === s.key}
          className={cn("flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors", step === s.key ? "bg-slate-900 text-white border-slate-900" : i < idx ? "bg-slate-100 border-slate-200 text-slate-700" : "border-slate-200 text-slate-500 hover:bg-slate-50")}>
          <span className="font-mono">{i < idx ? <Check className="w-3 h-3" /> : i + 1}</span>{s.label}
        </button>
      ))}
    </div>
  );
}

function N244Dialog({ caseRow, onClose, onDone }) {
  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState(null);
  const [step, setStep] = useState("order");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiStage, setAiStage] = useState("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const [aiResult, setAiResult] = useState(null);
  const [result, setResult] = useState(null);
  const [sent, setSent] = useState([]);
  const [composedFor, setComposedFor] = useState(null);
  const idx = STEPS.findIndex((s) => s.key === step);
  const blocked = !!form && step === "details" && !form.n244.claim_number?.trim() && !form.override_reason?.trim();

  const load = useCallback(async () => {
    try { const c = await getN244Context(caseRow.case_id); setCtx(c); setForm((f) => f || initialForm(c)); }
    catch (e) { toast.error(errMsg(e)); onClose(); }
  }, [caseRow.case_id, onClose]);
  useEffect(() => { load(); }, [load]);

  /** Fill with AI — apply the proposal over the form. Court / case number / parties already on the file win; the AI's reading goes in the notes. */
  const ai = async (instructions) => {
    setAiBusy(true);
    setAiStage("starting");
    try {
      const run = await n244AiDraft(caseRow.case_id, { file_keys: form.related_keys, order_type: form.order_type, instructions, fields: form.fields, n244: form.n244 });
      let r = null;
      let misses = 0;
      for (let i = 0; i < 200 && alive.current; i += 1) {
        await new Promise((res) => setTimeout(res, 2500));
        let s;
        try { s = await n244AiRun(caseRow.case_id, run.run_id); misses = 0; }
        catch (e) { if (++misses >= 4) throw e; continue; }
        if (!alive.current) return;
        setAiStage(s.stage || "");
        if (s.status === "failed") throw new Error(s.error || "The AI draft failed");
        if (s.status === "done") { r = s.result; break; }
      }
      if (!alive.current) return;
      if (!r) throw new Error("The AI draft is taking too long — try again with fewer documents");
      const byKey = Object.fromEntries(ctx.files.map((f) => [f.key, f]));
      const notes = [...(r.notes || [])];
      const facts = {};
      for (const k of ["court_name", "claim_number", "defendants", "debtor_name"]) {
        const v = (r.n244 || {})[k];
        if (!v) continue;
        if (!form.n244[k]?.trim()) facts[k] = v;
        else if (String(v).trim().toLowerCase() !== String(form.n244[k]).trim().toLowerCase()) notes.push(`Document reads ${k.replace("_", " ")} "${v}" — the form keeps "${form.n244[k]}" from the file; change it by hand if the document is right.`);
      }
      for (const k of ["hearing_mode", "hearing_date", "hearing_time"]) if ((r.n244 || {})[k]) facts[k] = r.n244[k];
      setForm((f) => ({
        ...f, order_type: r.order_type, fields: { ...f.fields, ...r.fields },
        n244: { ...f.n244, ...facts, draft_order: r.n244?.draft_order ?? f.n244.draft_order, q3: r.q3 || f.n244.q3, q10: r.q10 || f.n244.q10 },
        draft_order: { include: r.n244?.draft_order ?? f.draft_order.include, paragraphs: r.draft_order_paragraphs?.length ? r.draft_order_paragraphs : f.draft_order.paragraphs },
        appendices: (r.appendix_keys || []).filter((k) => byKey[k]).map((k) => ({ key: k, name: byKey[k].name })),
        ai_meta: { model: r.model, confidence: r.confidence, notes, evidence: r.evidence, read: r.read, at: new Date().toISOString() },
      }));
      setComposedFor(r.order_type);
      setAiResult({ ...r, notes });
      toast.success(`AI proposal applied — ${ctx.order_types.find((o) => o.key === r.order_type)?.label}. Review every field on the next step.`);
      setStep("details");
    } catch (e) { if (alive.current) toast.error(errMsg(e)); } finally { if (alive.current) { setAiBusy(false); setAiStage(""); } }
  };

  /** Leaving step 1 without the AI: build the house-style wording from the guided fields so Q3 / Q10 match the order type chosen. */
  const go = async (k) => {
    const to = STEPS.findIndex((s) => s.key === k);
    if (blocked && to > idx) { toast.warning("No insolvency / claim number — give the override reason first, or enter the number."); return; }
    if (step === "order" && k === "details" && form && ((!form.n244.q3 && !form.n244.q10) || composedFor !== form.order_type)) {
      try {
        const r = await n244Compose(caseRow.case_id, { order_type: form.order_type, fields: form.fields, n244: form.n244 });
        setForm((f) => ({ ...f, n244: { ...f.n244, q3: r.q3, q10: r.q10 }, draft_order: { ...f.draft_order, paragraphs: r.draft_order } }));
        setComposedFor(form.order_type);
      } catch { /* the handler can still type */ }
    }
    setStep(k);
  };
  const ot = ctx && form ? ctx.order_types.find((o) => o.key === form.order_type) : null;

  return (
    <Dialog open onOpenChange={(v) => !v && (onClose(), onDone?.())}>
      <DialogContent className="max-w-5xl max-h-[94vh] overflow-y-auto" aria-describedby={undefined} data-testid="n244-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            <Gavel className="w-4 h-4 text-violet-600" />N244 — Application Notice
            <span className="text-[11px] font-normal text-muted-foreground">{caseRow.loan_application_id} · {caseRow.customer_name}{ot ? ` · ${ot.label}` : ""}</span>
            <span className="flex-1" />
            {result && <span className="text-[10px] font-normal text-emerald-700" data-testid="n244-generated-badge">generated</span>}
          </DialogTitle>
        </DialogHeader>
        <StepBar step={step} setStep={go} locked={aiBusy} />
        {blocked && (
          <div className="rounded border border-amber-400 bg-amber-50 p-2.5 text-[11px] text-amber-950 leading-relaxed" data-testid="n244-claim-warning">
            <b>No insolvency / claim number.</b> An N244 is an application <i>in</i> existing proceedings — Medway returned ours of 5 August 2026 for exactly this.
            Enter the number from the petition / claim, or give the reason below for filing without one (it is written on the timeline with the application).
          </div>
        )}
        {!ctx || !form ? <div className="py-10 flex items-center justify-center text-xs text-slate-500" data-testid="n244-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading the file…</div> : (
          <div className="pt-1">
            {step === "order" && <N244OrderStep ctx={ctx} form={form} setForm={setForm} onAi={ai} aiBusy={aiBusy} aiStage={aiStage} aiResult={aiResult} />}
            {step === "details" && <N244DetailsStep ctx={ctx} form={form} setForm={setForm} />}
            {step === "bundle" && <N244BundleStep ctx={ctx} form={form} setForm={setForm} result={result} setResult={(r) => { setResult(r); load(); onDone?.(); }} />}
            {step === "send" && <N244SendStep caseRow={caseRow} form={form} result={result} sent={sent} setSent={setSent} />}
          </div>
        )}
        {ctx && form && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => { onClose(); onDone?.(); }} data-testid="n244-close">Close</Button>
            <div className="flex gap-2">
              {idx > 0 && <Button variant="outline" size="sm" onClick={() => go(STEPS[idx - 1].key)} disabled={aiBusy} data-testid="n244-back">← Back</Button>}
              {idx < STEPS.length - 1 && <Button size="sm" onClick={() => go(STEPS[idx + 1].key)} disabled={aiBusy || blocked} title={blocked ? "Enter the case number or an override reason" : undefined} data-testid="n244-next">{step === "order" ? "Fill in by hand →" : "Next →"}</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default desktopOnly(N244Dialog, "The N244 wizard");
